import { QueryCache, QueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { isApiError } from "./errors";

const STALE_TIME_MS = 60 * 1000; // 1 minute
const RETRY_COUNT = 2;
const RETRY_DELAY_MS = 1000;

function getStatus(error: unknown): number | null {
  if (isApiError(error)) {
    if (typeof error.status === "number" || error.status === null) {
      return error.status;
    }
  }

  const axiosStatus = (error as { response?: { status?: number } })?.response?.status;
  return typeof axiosStatus === "number" ? axiosStatus : null;
}

/**
 * Don't retry on 4xx (client errors) or when the request is not retryable.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= RETRY_COUNT) return false;
  const status = getStatus(error);
  if (status != null && status >= 400 && status < 500) return false;
  return true;
}

function queryCacheOnError(error: unknown): void {
  const status = getStatus(error);
  if (status === 401) {
    // TODO: redirect to login or clear auth (e.g. useAuth().logout())
    return;
  }
  if (status != null && status >= 500) {
    toast.error("Something went wrong. Please try again.");
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_MS,
      retry: shouldRetry,
      retryDelay: RETRY_DELAY_MS,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: queryCacheOnError,
  }),
});
